import styled from 'styled-components'
import { StyledFilterButtonProps, FilterButtonBadgeProps } from './FilterButton.types'

const getVariantColors = (variant: string, theme: any, eventType?: string) => {
  // Event types override variants
  if (eventType) {
    switch (eventType) {
      case 'transaction':
        return {
          color: theme.colors.primary,
          background: theme.colors.tags.blue,
          hoverBackground: 'rgba(2, 148, 223, 0.15)',
          activeBackground: theme.colors.primary,
          activeColor: theme.colors.text.inverse,
        }
      case 'rental':
        return {
          color: '#805AD5',
          background: theme.colors.tags.purple,
          hoverBackground: 'rgba(128, 90, 213, 0.15)',
          activeBackground: '#805AD5',
          activeColor: theme.colors.text.inverse,
        }
      case 'permit':
        return {
          color: theme.colors.warning,
          background: theme.colors.tags.orange,
          hoverBackground: 'rgba(255, 149, 0, 0.15)',
          activeBackground: theme.colors.warning,
          activeColor: theme.colors.text.inverse,
        }
      case 'sale':
        return {
          color: theme.colors.success,
          background: theme.colors.tags.green,
          hoverBackground: 'rgba(0, 179, 65, 0.15)',
          activeBackground: theme.colors.success,
          activeColor: theme.colors.text.inverse,
        }
    }
  }

  // Standard variants
  switch (variant) {
    case 'primary':
      return {
        color: theme.colors.primary,
        background: theme.colors.tags.blue,
        hoverBackground: 'rgba(2, 148, 223, 0.15)',
        activeBackground: theme.colors.primary,
        activeColor: theme.colors.text.inverse,
      }
    case 'success':
      return {
        color: theme.colors.success,
        background: theme.colors.tags.green,
        hoverBackground: 'rgba(0, 179, 65, 0.15)',
        activeBackground: theme.colors.success,
        activeColor: theme.colors.text.inverse,
      }
    case 'warning':
      return {
        color: theme.colors.warning,
        background: theme.colors.tags.orange,
        hoverBackground: 'rgba(255, 149, 0, 0.15)',
        activeBackground: theme.colors.warning,
        activeColor: theme.colors.text.inverse,
      }
    case 'danger':
      return {
        color: theme.colors.danger,
        background: theme.colors.tags.red,
        hoverBackground: 'rgba(255, 59, 48, 0.15)',
        activeBackground: theme.colors.danger,
        activeColor: theme.colors.text.inverse,
      }
    case 'purple':
      return {
        color: '#805AD5',
        background: theme.colors.tags.purple,
        hoverBackground: 'rgba(128, 90, 213, 0.15)',
        activeBackground: '#805AD5',
        activeColor: theme.colors.text.inverse,
      }
    default:
      return {
        color: theme.colors.primary,
        background: theme.colors.tags.blue,
        hoverBackground: 'rgba(2, 148, 223, 0.15)',
        activeBackground: theme.colors.primary,
        activeColor: theme.colors.text.inverse,
      }
  }
}

export const StyledFilterButton = styled.button<StyledFilterButtonProps>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.components.tag.padding};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all ${({ theme }) => theme.transitions.fast};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  font-size: ${({ theme }) => theme.components.tag.fontSize};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  line-height: 1;
  white-space: nowrap;
  user-select: none;

  ${({ theme, $variant, $eventType, $active, $disabled }) => {
    const colors = getVariantColors($variant, theme, $eventType)

    return `
      color: ${$active ? colors.activeColor : colors.color};
      background-color: ${$active ? colors.activeBackground : colors.background};
      opacity: ${$disabled ? 0.5 : 1};

      &:hover:not(:disabled) {
        background-color: ${$active ? colors.activeBackground : colors.hoverBackground};
        transform: translateY(-1px);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }

      &:focus-visible {
        outline: none;
        box-shadow: ${theme.shadows.focus};
      }
    `
  }}

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.micro};
    padding: 3px 8px;
    gap: ${({ theme }) => theme.spacing.micro};
  }

  /* Mobile: Ensure adequate touch target */
  @media (max-width: 1023px) {
    min-height: 36px;
    padding: 8px 12px;
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover:not(:disabled) {
      transform: none;
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }
  }
`

export const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.components.tag.iconSize};
  line-height: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 10px;
  }
`

export const LabelText = styled.span`
  font-weight: inherit;
  color: inherit;
`

export const CountBadge = styled.span<FilterButtonBadgeProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  line-height: 1;

  ${({ theme, $variant, $eventType, $active }) => {
    const colors = getVariantColors($variant, theme, $eventType)

    return `
      background-color: ${$active ? 'rgba(255, 255, 255, 0.2)' : colors.color};
      color: ${$active ? colors.activeColor : theme.colors.text.inverse};
    `
  }}

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: 16px;
    height: 16px;
    font-size: 9px;
    padding: 0 3px;
  }
`
